'use client'

import { useEffect, useState, useRef } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
    createSpecialty,
    updateSpecialty,
    getSpecialty,
    getSpecialtiesOnlyByOrganization,
} from '@/lib/api/specialties/specialties-api'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdMedicalServices, MdSubdirectoryArrowRight } from 'react-icons/md'
import { filterCommonSpecialties } from '@/lib/constants/common-specialties'

export default function SpecialtyFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams()
    const [searchParams] = useSearchParams()
    const isEdit = !!id
    const isSubspecialtyMode = searchParams.get('tipo') === 'subespecialidade'

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(isEdit)
    const [name, setName] = useState('')
    const [isSubspecialty, setIsSubspecialty] = useState(isSubspecialtyMode)
    const [parentId, setParentId] = useState<number | null>(null)
    const [parentSpecialties, setParentSpecialties] = useState<Specialty[]>([])
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadParentSpecialties()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (isEdit && id) {
            loadSpecialty(parseInt(id))
        }
    }, [id, isEdit])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (name.trim().length >= 2 && showSuggestions) {
            const filtered = filterCommonSpecialties(name, [], 10)
            setSuggestions(filtered)
        } else {
            setSuggestions([])
        }
    }, [name, showSuggestions])

    const loadParentSpecialties = async () => {
        if (!user?.companyId) return

        try {
            const response = await getSpecialtiesOnlyByOrganization(user.organizationId)
            if (response.success) {
                setParentSpecialties(response.result)
            }
        } catch {
            // silently fail
        }
    }

    const loadSpecialty = async (specialtyId: number) => {
        setLoadingData(true)
        try {
            const response = await getSpecialty(specialtyId)
            if (response.success && response.result) {
                setName(response.result.name)
                if (response.result.parentId) {
                    setIsSubspecialty(true)
                    setParentId(response.result.parentId)
                }
            } else {
                toast.error('Especialidade não encontrada')
                navigate('/registrations/specialties')
            }
        } catch {
            toast.error('Erro ao carregar especialidade')
            navigate('/registrations/specialties')
        } finally {
            setLoadingData(false)
        }
    }

    const handleSelectSuggestion = (suggestion: string) => {
        setName(suggestion)
        setShowSuggestions(false)
        inputRef.current?.focus()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!name.trim()) {
            toast.warning('Preencha o nome da especialidade')
            return
        }

        if (isSubspecialty && !parentId) {
            toast.error('Selecione a especialidade principal para a subespecialidade')
            return
        }

        setLoading(true)

        try {
            let response

            if (isEdit && id) {
                response = await updateSpecialty(parseInt(id), {
                    name: name.trim(),
                    parentId: isSubspecialty ? parentId : null,
                })
            } else {
                response = await createSpecialty({
                    name: name.trim(),
                    organizationId: user.organizationId,
                    parentId: isSubspecialty ? parentId : undefined,
                })
            }

            if (response.success) {
                toast.success(
                    isEdit
                        ? isSubspecialty
                            ? 'Subespecialidade atualizada com sucesso'
                            : 'Especialidade atualizada com sucesso'
                        : isSubspecialty
                            ? 'Subespecialidade criada com sucesso'
                            : 'Especialidade criada com sucesso'
                )
                navigate('/registrations/specialties')
            } else {
                toast.error(
                    response.error ||
                        (isEdit
                            ? 'Erro ao atualizar especialidade'
                            : 'Erro ao criar especialidade')
                )
            }
        } catch {
            toast.error(
                isEdit
                    ? 'Erro ao atualizar especialidade'
                    : 'Erro ao criar especialidade'
            )
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando especialidade...
                </p>
            </div>
        )
    }

    const title = isSubspecialty
        ? isEdit
            ? 'Editar Subespecialidade'
            : 'Nova Subespecialidade'
        : isEdit
            ? 'Editar Especialidade'
            : 'Nova Especialidade'

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/specialties')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        {isSubspecialty ? (
                            <MdSubdirectoryArrowRight className="text-2xl text-white" />
                        ) : (
                            <MdMedicalServices className="text-2xl text-white" />
                        )}
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {title}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="flex flex-col gap-4 max-w-md">
                        {!isEdit && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={isSubspecialty}
                                        onChange={e => {
                                            setIsSubspecialty(e.target.checked)
                                            if (!e.target.checked) {
                                                setParentId(null)
                                            }
                                        }}
                                    />
                                    <span className="label-text font-medium">
                                        Esta é uma subespecialidade
                                    </span>
                                </label>
                            </div>
                        )}

                        {isSubspecialty && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Especialidade principal <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={parentId ?? ''}
                                    onChange={e =>
                                        setParentId(
                                            e.target.value ? parseInt(e.target.value, 10) : null
                                        )
                                    }
                                    required
                                >
                                    <option value="">Selecione a especialidade principal</option>
                                    {parentSpecialties.map(spec => (
                                        <option key={spec.id} value={spec.id}>
                                            {spec.name}
                                        </option>
                                    ))}
                                </select>
                                {parentSpecialties.length === 0 && (
                                    <label className="label">
                                        <span className="label-text-alt text-warning">
                                            Nenhuma especialidade cadastrada. Crie uma especialidade primeiro.
                                        </span>
                                    </label>
                                )}
                            </div>
                        )}

                        <div className="form-control w-full" ref={dropdownRef}>
                            <label className="label">
                                <span className="label-text font-medium">
                                    {isSubspecialty ? 'Nome da subespecialidade' : 'Nome'}{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder={
                                    isSubspecialty
                                        ? 'Ex: Cirurgia de Quadril'
                                        : 'Digite o nome da especialidade'
                                }
                                className="input input-bordered w-full"
                                value={name}
                                onChange={e => {
                                    setName(e.target.value)
                                    setShowSuggestions(true)
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                required
                            />
                            {showSuggestions && suggestions.length > 0 && !isSubspecialty && (
                                <div className="relative">
                                    <ul className="absolute z-50 top-1 w-full bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {suggestions.map((suggestion, index) => (
                                            <li
                                                key={index}
                                                className="px-4 py-2 hover:bg-base-200 cursor-pointer text-sm transition-colors"
                                                onClick={() =>
                                                    handleSelectSuggestion(suggestion)
                                                }
                                            >
                                                {suggestion}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {!isSubspecialty && (
                                <label className="label">
                                    <span className="label-text-alt text-base-content/50">
                                        Digite pelo menos 2 caracteres para ver
                                        sugestões de especialidades comuns
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/registrations/specialties')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1 gap-2"
                        disabled={loading || !name.trim() || (isSubspecialty && !parentId)}
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                {isEdit ? 'Atualizar' : isSubspecialty ? 'Criar Subespecialidade' : 'Salvar'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
